import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import type { BillingMembership, BillingStatus, ExperienceLevel, Gender, Locale, OnboardingIntent } from '../api/types';
import { GenderPicker } from '../components/GenderPicker';
import { MainPracticePicker } from '../components/MainPracticePicker';
import { TraditionPicker } from '../components/TraditionPicker';
import { useAuth } from '../auth/AuthContext';
import { useUser } from '../state/UserContext';
import { setAppLanguage } from '../i18n';
import { ALL_THEME_IDS, THEME_DISPLAY_NAMES, THEMES, type Tradition, type ThemeId } from '../theme/tokens';

type Tab = 'profile' | 'traditions' | 'appearance' | 'billing';

const TABS: { id: Tab; labelKey: string; icon: string }[] = [
  { id: 'profile', labelKey: 'settings.tabs.profile', icon: '👤' },
  { id: 'traditions', labelKey: 'settings.tabs.traditions', icon: '🧭' },
  { id: 'appearance', labelKey: 'settings.tabs.appearance', icon: '🎨' },
  { id: 'billing', labelKey: 'settings.tabs.billing', icon: '💳' },
];

// Each language's own name, in its own script -- shown in every locale identically (a language
// picker labels its options in the native name, not translated into whatever's currently active).
const LOCALE_NAMES: Record<Locale, string> = { en: 'English', es: 'Español', hi: 'हिन्दी', zh: '中文' };
const ALL_LOCALES: Locale[] = ['en', 'es', 'hi', 'zh'];

// Settings > Billing helpers (code-review fix F3).
const MEMBERSHIP_STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  trialing: 'Trial',
  past_due: 'Payment overdue',
  canceled: 'Cancelled',
  cancelled: 'Cancelled',
  expired: 'Expired',
  completed: 'Ended',
  inactive: 'Ended',
};

function membershipStatusLabel(m: BillingMembership): string {
  return (m.status && MEMBERSHIP_STATUS_LABELS[m.status]) || (m.entitled ? 'Active' : 'Ended');
}

function formatBillingDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export function Settings() {
  const { me, updateMe, refreshQuiet } = useUser();
  const { logout } = useAuth();
  const { t } = useTranslation();
  const [localeSaving, setLocaleSaving] = useState<Locale | null>(null);

  const [tab, setTab] = useState<Tab>(() => {
    const params = new URLSearchParams(window.location.search);
    // `?tab=billing`: deep link from a Premium-locked practice's explanation (review fix 2026-09-10).
    return params.get('upgraded') || params.get('tab') === 'billing' ? 'billing' : 'profile';
  });
  const [displayName, setDisplayName] = useState(me?.displayName ?? '');
  const [traditions, setTraditions] = useState<Tradition[]>(me?.traditions ?? []);
  const [primaryTradition, setPrimaryTradition] = useState<Tradition | null>(me?.primaryTradition ?? null);
  const [gender, setGender] = useState<Gender | null>(me?.gender ?? null);
  const [intent, setIntent] = useState<OnboardingIntent | null>(me?.intent ?? null);
  const [experienceLevel, setExperienceLevel] = useState<ExperienceLevel | null>(me?.experienceLevel ?? null);
  const [timeBudgetMinutes, setTimeBudgetMinutes] = useState<number | null>(me?.timeBudgetMinutes ?? null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [themeSaving, setThemeSaving] = useState<ThemeId | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [returnedFromCheckout, setReturnedFromCheckout] = useState(
    () => new URLSearchParams(window.location.search).get('upgraded') === '1'
  );
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [billingStatusLoading, setBillingStatusLoading] = useState(false);
  const [billingStatusError, setBillingStatusError] = useState<string | null>(null);

  // Settings > Billing (code-review fix F3): the membership details behind `tier` -- paid Whop
  // membership(s) and/or an Omnist admin grant. Reloaded when the tab opens or the tier changes
  // (e.g. once the post-checkout polling below sees Premium land).
  const loadBillingStatus = useCallback(async () => {
    setBillingStatusLoading(true);
    setBillingStatusError(null);
    try {
      setBillingStatus(await api.getBillingStatus());
    } catch (e) {
      setBillingStatusError(e instanceof Error ? e.message : 'Could not load your membership details.');
    } finally {
      setBillingStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'billing') void loadBillingStatus();
  }, [tab, me?.tier, loadBillingStatus]);

  // Coming back from a real Whop checkout: the redirect itself never grants Premium (only a
  // verified webhook does, see backend/app/handlers/billing.py) -- so poll GET /api/me a few
  // times rather than trusting the URL, in case the webhook hasn't landed yet.
  const polledRef = useRef(false);
  useEffect(() => {
    if (!returnedFromCheckout || polledRef.current) return;
    polledRef.current = true;
    window.history.replaceState({}, '', window.location.pathname);
    let cancelled = false;
    (async () => {
      for (let attempt = 0; attempt < 6 && !cancelled; attempt++) {
        const updated = await refreshQuiet();
        if (updated?.tier === 'premium') return;
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [returnedFromCheckout, refreshQuiet]);

  const handleUpgrade = async () => {
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const { checkoutUrl } = await api.createCheckout();
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }
      // Mock mode applies the upgrade immediately with no external URL to redirect to.
      await refreshQuiet();
      setReturnedFromCheckout(true);
    } catch (e) {
      setCheckoutError(e instanceof Error ? e.message : 'Could not start checkout.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const effectivePrimary =
        traditions.length === 1
          ? traditions[0]
          : traditions.includes(primaryTradition as Tradition)
            ? primaryTradition
            : null;
      await updateMe({
        displayName,
        traditions,
        primaryTradition: effectivePrimary,
        gender,
        intent,
        experienceLevel,
        timeBudgetMinutes,
      });
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save your settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleLeaderboardToggle = async () => {
    if (!me) return;
    setError(null);
    try {
      await updateMe({ leaderboardOptIn: !me.leaderboardOptIn });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update your leaderboard setting.');
    }
  };

  const handleThemeSelect = async (themePreference: ThemeId) => {
    if (!me || me.themePreference === themePreference) return;
    setError(null);
    setThemeSaving(themePreference);
    try {
      await updateMe({ themePreference });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update your theme.');
    } finally {
      setThemeSaving(null);
    }
  };

  const handleLocaleSelect = async (locale: Locale) => {
    if (!me || me.locale === locale) return;
    setError(null);
    setLocaleSaving(locale);
    try {
      await updateMe({ locale });
      setAppLanguage(locale);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update your language.');
    } finally {
      setLocaleSaving(null);
    }
  };

  // Review fix F6: WAI-ARIA tabs keyboard model -- arrow keys / Home / End move between the four
  // sections (automatic activation); only the selected tab sits in the Tab order.
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const handleTabKeyDown = (e: { key: string; preventDefault: () => void }, index: number) => {
    const last = TABS.length - 1;
    const next =
      e.key === 'ArrowRight' || e.key === 'ArrowDown'
        ? index === last ? 0 : index + 1
        : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
          ? index === 0 ? last : index - 1
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? last
              : null;
    if (next === null) return;
    e.preventDefault();
    setTab(TABS[next].id);
    tabRefs.current[next]?.focus();
  };

  const previewTradition = primaryTradition ?? (traditions.length === 1 ? traditions[0] : null);

  const isPremium = me?.tier === 'premium';
  const memberships = billingStatus?.memberships ?? [];
  const premiumSource =
    !billingStatus || !isPremium
      ? null
      : billingStatus.paidPremium && billingStatus.adminPremiumGrant
        ? 'Through your Whop membership, plus access added by the Omnist team.'
        : billingStatus.paidPremium
          ? 'Through your Whop membership.'
          : billingStatus.adminPremiumGrant
            ? 'Added to your account by the Omnist team, at no charge.'
            : null;

  return (
    // Wide screens (CSS): the tab list becomes a sticky vertical rail beside the panel.
    <div className="settings-page">
      <h1 className="page-title">{t('settings.title')}</h1>

      <div className="settings-tabs" role="tablist" aria-label={t('settingsShell.tabsLabel')}>
        {TABS.map((tabItem, index) => {
          const isActive = tab === tabItem.id;
          return (
            <button
              key={tabItem.id}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              id={`settings-tab-${tabItem.id}`}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls="settings-tabpanel"
              tabIndex={isActive ? 0 : -1}
              className={`settings-tab${isActive ? ' active' : ''}`}
              onClick={() => setTab(tabItem.id)}
              onKeyDown={(e) => handleTabKeyDown(e, index)}
            >
              <span className="settings-tab-icon" aria-hidden="true">{tabItem.icon}</span>
              <span className="settings-tab-label">{t(tabItem.labelKey)}</span>
            </button>
          );
        })}
      </div>

      <div id="settings-tabpanel" role="tabpanel" aria-labelledby={`settings-tab-${tab}`} className="settings-tabpanel">
      {tab === 'profile' && (
        <div className="settings-panel">
          <div className="field settings-name-field">
            <label htmlFor="displayName">Display name</label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
            />
          </div>

          <div className="section-title">Your practice</div>
          <p className="page-subtitle" style={{ marginBottom: 12 }}>
            Shapes your Today recommendation on Home -- editable any time, not just at signup.
          </p>
          <div className="settings-fields-grid">
            <div className="field">
              <label htmlFor="intent">What brings you here</label>
              <select
                id="intent"
                value={intent ?? ''}
                onChange={(e) => setIntent((e.target.value || null) as OnboardingIntent | null)}
              >
                <option value="">Prefer not to say</option>
                <option value="deepen">Deepen my tradition</option>
                <option value="explore">Explore spirituality</option>
                <option value="routine">Build my routine</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="experienceLevel">Your experience</label>
              <select
                id="experienceLevel"
                value={experienceLevel ?? ''}
                onChange={(e) => setExperienceLevel((e.target.value || null) as ExperienceLevel | null)}
              >
                <option value="">Prefer not to say</option>
                <option value="new">New to this</option>
                <option value="some">Some experience</option>
                <option value="experienced">Experienced</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="timeBudget">Daily time budget (minutes)</label>
              <input
                id="timeBudget"
                type="number"
                inputMode="numeric"
                min={3}
                max={120}
                value={timeBudgetMinutes ?? ''}
                onChange={(e) => setTimeBudgetMinutes(e.target.value ? Number(e.target.value) : null)}
                placeholder="No limit"
                aria-describedby="timeBudget-hint"
              />
              <p id="timeBudget-hint" className="field-hint">{t('settingsShell.timeBudgetHint')}</p>
            </div>
          </div>

          <div className="section-title">Your guide</div>
          <p className="page-subtitle" style={{ marginBottom: 12 }}>
            Who greets you on Home and walks you through your practices.
          </p>
          <GenderPicker selected={gender} onChange={setGender} tradition={previewTradition} />

          <div className="toggle-row">
            <div>
              <div className="toggle-row-label">Leaderboard opt-in</div>
              <div className="practice-item-meta">Show your streak on the public leaderboard.</div>
            </div>
            <button
              type="button"
              className={`switch${me?.leaderboardOptIn ? ' on' : ''}`}
              role="switch"
              aria-checked={Boolean(me?.leaderboardOptIn)}
              onClick={handleLeaderboardToggle}
              disabled={!me}
            >
              <span className="knob" />
            </button>
          </div>
        </div>
      )}

      {tab === 'traditions' && (
        <div className="settings-panel">
          <p className="page-subtitle" style={{ marginBottom: 12 }}>
            These shape your daily quotes, practices, and library.
          </p>
          <p className="field-hint settings-explore-hint">{t('settingsShell.exploreAllHint')}</p>
          <TraditionPicker selected={traditions} onChange={setTraditions} />

          {traditions.length > 1 && (
            <>
              <div className="section-title">Main practice</div>
              <p className="page-subtitle" style={{ marginBottom: 12 }}>
                The one your guide will be dressed for, and what we&rsquo;ll feature first on
                Home. The rest still shape your quotes and practices as secondary traditions.
              </p>
              <MainPracticePicker options={traditions} selected={primaryTradition} onChange={setPrimaryTradition} />
            </>
          )}
        </div>
      )}

      {tab === 'appearance' && (
        <div className="settings-panel">
          <p className="page-subtitle" style={{ marginBottom: 12 }}>
            Your theme is independent of your traditions &mdash; pick whichever palette feels
            right. Saved instantly.
          </p>
          <div className="theme-grid">
            {ALL_THEME_IDS.map((id) => {
              const tokens = THEMES[id];
              const isSelected = me?.themePreference === id;
              return (
                <button
                  key={id}
                  type="button"
                  className={`theme-option${isSelected ? ' selected' : ''}`}
                  onClick={() => handleThemeSelect(id)}
                  disabled={!me || themeSaving !== null}
                  aria-pressed={isSelected}
                >
                  <span className="theme-option-swatches" aria-hidden="true">
                    <span className="swatch" style={{ background: tokens.primary }} />
                    <span className="swatch" style={{ background: tokens.secondary }} />
                    <span className="swatch" style={{ background: tokens.background }} />
                  </span>
                  <span className="theme-option-name">{THEME_DISPLAY_NAMES[id]}</span>
                  {isSelected && <span className="theme-option-check" aria-hidden="true">&#10003;</span>}
                </button>
              );
            })}
          </div>

          <div className="section-title" style={{ marginTop: 24 }}>{t('settings.language')}</div>
          <p className="page-subtitle" style={{ marginBottom: 12 }}>{t('settings.languageHelp')}</p>
          <div className="theme-grid">
            {ALL_LOCALES.map((locale) => {
              const isSelected = (me?.locale ?? 'en') === locale;
              return (
                <button
                  key={locale}
                  type="button"
                  className={`theme-option${isSelected ? ' selected' : ''}`}
                  onClick={() => handleLocaleSelect(locale)}
                  disabled={!me || localeSaving !== null}
                  aria-pressed={isSelected}
                >
                  <span className="theme-option-name">{LOCALE_NAMES[locale]}</span>
                  {isSelected && <span className="theme-option-check" aria-hidden="true">&#10003;</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'billing' && (
        <div className="settings-panel billing-panel">
          <section className="themed-card" aria-labelledby="billing-plan-heading">
            <div className="billing-plan-header">
              <h2 id="billing-plan-heading" className="billing-heading">Omnist Premium</h2>
              <p className="billing-price">
                <span className="billing-price-amount">$9.99</span> per month
              </p>
            </div>
            <p className="billing-note">
              Billed monthly through Whop, our payment provider. You can cancel any time from your
              Whop account.
            </p>
            <div className="billing-compare">
              <div className="billing-compare-item">
                <h3 className="billing-compare-title">Free, for good</h3>
                <p className="billing-note">
                  Levels 1&ndash;5 of every pillar, in every tradition. There&rsquo;s no trial period
                  and no card needed. It&rsquo;s a complete introduction, not a locked demo, and
                  Streak Shields work the same on both plans.
                </p>
              </div>
              <div className="billing-compare-item">
                <h3 className="billing-compare-title">Premium</h3>
                <p className="billing-note">Every level after level 5, in any pillar, as it&rsquo;s published.</p>
              </div>
            </div>
            <p className="billing-note billing-depth-note">
              What exists today: levels beyond 5 are written for five pillars. Christian prayer,
              Hindu meditation, Islamic prayer and Jewish prayer go up to level 8, and Buddhist
              meditation to level 6. Most other pillars have only their first level so far, so
              Premium doesn&rsquo;t add anything there yet. Browse Practices to see what&rsquo;s
              available before you upgrade.
            </p>
          </section>

          <section className="themed-card" aria-labelledby="billing-status-heading" aria-live="polite">
            <h2 id="billing-status-heading" className="billing-heading">Your plan</h2>
            <p className="billing-status-row">
              <span className={`billing-badge${isPremium ? ' premium' : ''}`}>{isPremium ? 'Premium' : 'Free'}</span>
              {premiumSource && <span>{premiumSource}</span>}
            </p>

            {billingStatusLoading && !billingStatus && <p className="billing-note">Loading membership details&hellip;</p>}
            {billingStatusError && (
              <p className="billing-note">
                We couldn&rsquo;t load your membership details right now. The plan shown above comes
                from your profile.{' '}
                <button type="button" className="btn btn-ghost btn-small" onClick={() => void loadBillingStatus()}>
                  Try again
                </button>
              </p>
            )}

            {memberships.length > 0 && (
              <ul className="billing-membership-list">
                {memberships.map((m) => (
                  <li key={m.membershipId} className="billing-membership">
                    <div className="toggle-row-label">Whop membership: {membershipStatusLabel(m)}</div>
                    {m.entitled && m.cancelAtPeriodEnd && (
                      <p className="billing-note">
                        Cancellation scheduled
                        {m.renewalPeriodEnd ? `. Premium stays active until ${formatBillingDate(m.renewalPeriodEnd)}.` : '.'}
                      </p>
                    )}
                    {m.entitled && !m.cancelAtPeriodEnd && m.renewalPeriodEnd && (
                      <p className="billing-note">Renews on {formatBillingDate(m.renewalPeriodEnd)}.</p>
                    )}
                    {m.entitled && m.paymentFailed && (
                      <p className="billing-note billing-warning">
                        Your last payment didn&rsquo;t go through. Update your payment method in Whop to keep Premium.
                      </p>
                    )}
                    {m.manageUrl && (
                      <a className="btn btn-secondary btn-small" href={m.manageUrl} target="_blank" rel="noopener noreferrer">
                        Manage this membership on Whop
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {memberships.length > 0 && !memberships.some((m) => m.manageUrl) && (
              <p className="billing-note">
                To cancel or change your payment method, sign in to Whop with the account you used at
                checkout and open your Omnist Premium membership there. Your Whop receipt email also
                links to it.
              </p>
            )}
          </section>

          {returnedFromCheckout && !isPremium && (
            <p className="billing-note" role="status">
              Thanks! Premium turns on as soon as Whop confirms your payment. This page will update
              by itself.
            </p>
          )}

          {checkoutError && <p className="error-text">{checkoutError}</p>}

          {!isPremium && (
            <div>
              <button
                type="button"
                className="btn btn-primary btn-block"
                onClick={handleUpgrade}
                disabled={checkoutLoading}
              >
                {checkoutLoading ? 'Starting checkout...' : 'Upgrade to Premium — $9.99/month'}
              </button>
              <p className="billing-note">
                This opens Whop&rsquo;s secure checkout. Premium turns on once Whop confirms your
                payment, usually within a few seconds.
              </p>
            </div>
          )}

          <section className="billing-family" aria-labelledby="billing-family-heading">
            <h2 id="billing-family-heading" className="billing-heading">Family plan: not available yet</h2>
            <p className="billing-note">
              We plan to let you add family members for $2.50/month each, up to five extra members.
              It isn&rsquo;t on sale yet: invitations, shared access and privacy between family
              members haven&rsquo;t been built. Nobody is charged for it.
            </p>
          </section>
        </div>
      )}

      </div>

      {/* Review fix F7: always-mounted live regions, so a new message is announced when it appears. */}
      <div role="alert" className="settings-messages">
        {error && <p className="error-text">{error}</p>}
      </div>
      <div role="status" className="settings-messages">
        {saved && <p className="saved-text">{t('common.saved')}</p>}
      </div>

      {tab !== 'appearance' && tab !== 'billing' && (
        <button type="button" className="btn btn-primary btn-block" onClick={handleSave} disabled={saving} style={{ marginTop: 16 }}>
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      )}

      <button type="button" className="btn btn-ghost btn-block" onClick={logout} style={{ marginTop: 12 }}>
        Log out
      </button>
    </div>
  );
}
